import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { Loader2, Mail, Lock, User, Shield, LockKeyhole, ShieldCheck, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import AlreadyRegisteredModal from './AlreadyRegisteredModal';
import WelcomeModal from './WelcomeModal';
import ForgotPasswordModal from './ForgotPasswordModal';
import SignupConfirmation from './SignupConfirmation';
import EmailVerificationCard from './EmailVerificationCard';

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4 mr-2" aria-hidden="true">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

interface EnhancedAuthFormProps {
  onSuccess: () => void;
  onClose: () => void;
}

const EnhancedAuthForm: React.FC<EnhancedAuthFormProps> = ({ onSuccess, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: ''
  });
  const [showAlreadyRegistered, setShowAlreadyRegistered] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [welcomeName, setWelcomeName] = useState('');
  const [isReturningUser, setIsReturningUser] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showSignupConfirmation, setShowSignupConfirmation] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [activeTab, setActiveTab] = useState('signin');
  const formRef = useRef<HTMLFormElement>(null);

  const { signUp, signIn, signInWithGoogle } = useAuth();

  // Poll for session when waiting for email confirmation
  useEffect(() => {
    if (!showSignupConfirmation) return;
    const interval = setInterval(async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        clearInterval(interval);
        setConfirmed(true);
        setTimeout(() => onSuccess(), 1500);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [showSignupConfirmation, onSuccess]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (showEmailVerification) setShowEmailVerification(false);
  };

  const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const extractFirstName = (fullName: string, email: string): string => {
    if (fullName && fullName.trim()) return fullName.trim().split(' ')[0];
    const emailName = email.split('@')[0].split('.')[0];
    return emailName.charAt(0).toUpperCase() + emailName.slice(1);
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.fullName.trim()) {
      toast({ title: "Name required", description: "Please enter your full name." });
      return;
    }
    if (!validateEmail(formData.email)) {
      toast({ title: "Invalid email", description: "Please enter a valid email address." });
      return;
    }
    if (formData.password.length < 6) {
      toast({ title: "Password too short", description: "Password must be at least 6 characters." });
      return;
    }
    setLoading(true);
    try {
      const { error } = await signUp(formData.email, formData.password, formData.fullName);
      if (error) {
        if (error.message.includes('already registered') || error.message.includes('already been registered') || error.message.includes('User already registered')) {
          setShowAlreadyRegistered(true);
        } else {
          toast({ title: "Registration unsuccessful", description: "Please check your details and try again." });
        }
      } else {
        setShowSignupConfirmation(true);
      }
    } catch {
      toast({ title: "Something went wrong", description: "Please try again in a moment." });
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateEmail(formData.email)) {
      toast({ title: "Invalid email", description: "Please enter a valid email address." });
      return;
    }
    if (!formData.password) {
      toast({ title: "Password required", description: "Please enter your password." });
      return;
    }
    setLoading(true);
    try {
      const { error } = await signIn(formData.email, formData.password);
      if (error) {
        if (error.message.toLowerCase().includes('email not confirmed') || error.message.toLowerCase().includes('email_not_confirmed')) {
          setShowEmailVerification(true);
        } else if (error.message.includes('Invalid login credentials')) {
          toast({ title: "Login unsuccessful", description: "Please verify your credentials or confirm your email." });
        } else {
          toast({ title: "Login unsuccessful", description: "Please verify your credentials and try again." });
        }
      } else {
        // Success micro-animation
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          const firstName = extractFirstName('', formData.email);
          setWelcomeName(firstName);
          setIsReturningUser(true);
          setShowWelcome(true);
        }, 300);
      }
    } catch {
      toast({ title: "Something went wrong", description: "Please try again in a moment." });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      const { error } = await signInWithGoogle();
      if (error) {
        if (error.message?.includes('provider is not enabled')) {
          toast({ title: "Google Sign-In unavailable", description: "Google authentication is not configured. Please use email." });
        } else {
          toast({ title: "Google Sign-In unsuccessful", description: "Please try again or use email sign-in." });
        }
        setGoogleLoading(false);
      }
    } catch {
      toast({ title: "Something went wrong", description: "Please try again in a moment." });
      setGoogleLoading(false);
    }
  };

  const handleAlreadyRegisteredSignIn = (firstName: string) => {
    setShowAlreadyRegistered(false);
    setWelcomeName(firstName);
    setIsReturningUser(true);
    setShowWelcome(true);
  };

  const handleWelcomeClose = () => {
    setShowWelcome(false);
    onSuccess();
  };

  if (showSignupConfirmation) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.25 }}
      >
        <Card className="w-full max-w-md border-0 bg-background/95 backdrop-blur">
          <CardContent className="px-6 pb-5 pt-5">
            <SignupConfirmation email={formData.email} confirmed={confirmed} />
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 20 : -20,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 20 : -20,
      opacity: 0,
    }),
  };

  const tabDirection = activeTab === 'signup' ? 1 : -1;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        <Card className="w-full max-w-md border-0 bg-background/95 backdrop-blur relative overflow-hidden">
          {/* Circuit board background */}
          <div className="absolute inset-0 circuit-bg pointer-events-none" />
          
          <CardHeader className="text-center space-y-3 pb-3 pt-5 px-6 relative">
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.05, type: "spring", stiffness: 200 }}
              className="mx-auto relative w-12 h-12"
            >
              {/* Ping ring */}
              <div className="absolute inset-0 bg-primary/20 rounded-lg animate-ping-slow" />
              <div className="relative w-12 h-12 bg-primary/15 rounded-lg flex items-center justify-center ring-1 ring-primary/20">
                <div className="w-6 h-6 bg-primary rounded opacity-80" />
              </div>
            </motion.div>
            
            <div>
              <CardTitle className="text-lg">Welcome to Auto Trade Plug</CardTitle>
              <CardDescription className="text-sm mt-1">
                Secure access to your trading environment
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent className="space-y-4 px-6 pb-5 relative">
            {/* Google Sign-In */}
            <Button
              variant="outline"
              onClick={handleGoogleSignIn}
              disabled={googleLoading || loading}
              className="w-full h-11 bg-white text-gray-800 border border-gray-300 hover:bg-gray-50 dark:bg-white/10 dark:text-white dark:border-white/20 dark:hover:bg-white/15 relative overflow-hidden group"
            >
              {googleLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Redirecting to Google...
                </>
              ) : (
                <>
                  <GoogleIcon />
                  Continue with Google
                </>
              )}
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700 pointer-events-none" />
            </Button>
            <p className="text-center text-[10px] text-muted-foreground/40 -mt-2">
              You'll be redirected to Google to complete sign-in
            </p>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border/50" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground/60">
                  Or continue with email
                </span>
              </div>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="signin">Sign In</TabsTrigger>
                <TabsTrigger value="signup">Sign Up</TabsTrigger>
              </TabsList>

              <AnimatePresence mode="wait" custom={tabDirection}>
                {/* Sign In */}
                {activeTab === 'signin' && (
                  <TabsContent value="signin" className="mt-4" forceMount>
                    <motion.div
                      key="signin"
                      custom={tabDirection}
                      variants={slideVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{ duration: 0.2, ease: 'easeOut' }}
                      className="space-y-4"
                    >
                      <form onSubmit={handleSignIn} ref={formRef} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="signin-email" className="text-sm">Email</Label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground/60" />
                            <Input id="signin-email" name="email" type="email" placeholder="Enter your email" value={formData.email} onChange={handleInputChange} className="pl-10 h-11 focus-glow" required disabled={loading} />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label htmlFor="signin-password" className="text-sm">Password</Label>
                            <button type="button" onClick={() => setShowForgotPassword(true)} className="text-xs text-primary/80 hover:text-primary transition-colors">
                              Forgot Password?
                            </button>
                          </div>
                          <div className="relative">
                            <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground/60" />
                            <Input id="signin-password" name="password" type="password" placeholder="Enter your password" value={formData.password} onChange={handleInputChange} className="pl-10 h-11 focus-glow" required disabled={loading} />
                          </div>
                        </div>

                        <div className={`transition-all duration-300 ease-in-out overflow-hidden ${showEmailVerification ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}>
                          <EmailVerificationCard email={formData.email} />
                        </div>

                        <Button type="submit" disabled={loading || showSuccess} className={`w-full h-11 ${loading ? 'btn-fill-loading' : ''}`}>
                          {showSuccess ? (
                            <motion.span
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="flex items-center gap-2 text-green-400"
                            >
                              <CheckCircle2 className="h-4 w-4" />
                              Success!
                            </motion.span>
                          ) : loading ? (
                            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Signing in...</>
                          ) : (
                            'Sign In'
                          )}
                        </Button>
                      </form>
                    </motion.div>
                  </TabsContent>
                )}

                {/* Sign Up */}
                {activeTab === 'signup' && (
                  <TabsContent value="signup" className="mt-4" forceMount>
                    <motion.div
                      key="signup"
                      custom={tabDirection}
                      variants={slideVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{ duration: 0.2, ease: 'easeOut' }}
                      className="space-y-4"
                    >
                      <form onSubmit={handleSignUp} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="signup-name" className="text-sm">Full Name</Label>
                          <div className="relative">
                            <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground/60" />
                            <Input id="signup-name" name="fullName" type="text" placeholder="Enter your full name" value={formData.fullName} onChange={handleInputChange} className="pl-10 h-11 focus-glow" required disabled={loading} />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="signup-email" className="text-sm">Email</Label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground/60" />
                            <Input id="signup-email" name="email" type="email" placeholder="Enter your email" value={formData.email} onChange={handleInputChange} className="pl-10 h-11 focus-glow" required disabled={loading} />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="signup-password" className="text-sm">Password</Label>
                          <div className="relative">
                            <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground/60" />
                            <Input id="signup-password" name="password" type="password" placeholder="Create a password" value={formData.password} onChange={handleInputChange} className="pl-10 h-11 focus-glow" required disabled={loading} />
                          </div>
                          <p className="text-[11px] text-muted-foreground/60">Must be at least 6 characters</p>
                        </div>

                        <Button type="submit" disabled={loading} className={`w-full h-11 ${loading ? 'btn-fill-loading' : ''}`}>
                          {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating Account...</>) : 'Create Account'}
                        </Button>
                      </form>
                    </motion.div>
                  </TabsContent>
                )}
              </AnimatePresence>
            </Tabs>

            {/* Security Trust Signals + Terms */}
            <div className="flex items-center justify-center gap-3 pt-1 flex-wrap">
              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground/50 badge-shimmer rounded px-1.5 py-0.5">
                <Shield className="w-3 h-3" />
                <span>Secure</span>
              </div>
              <div className="w-px h-3 bg-border/30" />
              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground/50 badge-shimmer rounded px-1.5 py-0.5">
                <LockKeyhole className="w-3 h-3" />
                <span>Encrypted</span>
              </div>
              <div className="w-px h-3 bg-border/30" />
              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground/50 badge-shimmer rounded px-1.5 py-0.5">
                <ShieldCheck className="w-3 h-3" />
                <span>256-bit AES</span>
              </div>
              <div className="w-px h-3 bg-border/30" />
              <button type="button" className="text-[11px] text-muted-foreground/50 hover:text-muted-foreground transition-colors underline underline-offset-2">
                Terms & Privacy
              </button>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      <AlreadyRegisteredModal isOpen={showAlreadyRegistered} onClose={() => setShowAlreadyRegistered(false)} email={formData.email} onSignInSuccess={handleAlreadyRegisteredSignIn} />
      <WelcomeModal isOpen={showWelcome} onClose={handleWelcomeClose} firstName={welcomeName} isReturningUser={isReturningUser} />
      <ForgotPasswordModal isOpen={showForgotPassword} onClose={() => setShowForgotPassword(false)} />
    </>
  );
};

export default EnhancedAuthForm;
